import {useLayoutEffect, useRef} from 'react';

export interface CenteredTextProps {
  children: string;
}

/**
 * Centers the glyph's ink — not its font box — in the parent flex container. Latin and Hebrew
 * letters sit differently in the line box and some (like `י`) are short marks, so box-centered
 * text drifts by a pixel or two, which shows at the small avatar sizes. A progressive enhancement:
 * where the browser can't measure text, the text stays centered by its line box.
 */
export function CenteredText({children}: CenteredTextProps) {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const center = () => {
      element.style.translate = getInkOffset(element, children);
    };
    center();

    // Metrics measured before the font loads are the fallback font's, so redo them once fonts are ready.
    // (`status` can't be trusted to skip this: it reads "loaded" until a font has been requested.)
    let isCancelled = false;
    void document.fonts?.ready.then(() => {
      if (!isCancelled) center();
    });
    return () => {
      isCancelled = true;
    };
  }, [children]);

  // An explicit line-height makes the baseline depend on the primary font only, which `getInkOffset` relies on.
  return (
    <span ref={ref} data-slot="centered-text" className="leading-none">
      {children}
    </span>
  );
}

let measuringContext: CanvasRenderingContext2D | null | undefined;

/** The `translate` that moves the text's ink center onto its line box center, in `em` so it scales with the font size. */
function getInkOffset(element: HTMLElement, text: string): string {
  measuringContext ??= document.createElement('canvas').getContext('2d');
  if (!measuringContext || !text) return '';

  const {fontStyle, fontWeight, fontSize, fontFamily} = getComputedStyle(element);
  measuringContext.font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
  const metrics = measuringContext.measureText(text);
  // Older browsers lack the font-level metrics; leave the text where the line box puts it.
  if (metrics.fontBoundingBoxAscent === undefined) return '';
  // line-height is the font size (`leading-none`)
  const size = parseFloat(fontSize);

  const inkCenterX = (metrics.actualBoundingBoxRight - metrics.actualBoundingBoxLeft) / 2;
  const inkHeight = metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent;

  // Layout rounds the ascent and descent to whole pixels and floors the half-leading when placing the baseline.
  const ascent = Math.round(metrics.fontBoundingBoxAscent);
  const descent = Math.round(metrics.fontBoundingBoxDescent);
  const baseline = ascent + Math.floor((size - ascent - descent) / 2);

  const x = metrics.width / 2 - inkCenterX;
  const y = inkHeight / 2 - (baseline - size / 2);
  return `${x / size}em ${y / size}em`;
}
